// ✅ NUEVO ARCHIVO: src/components/UploadProgress.tsx
import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Clock, Upload, Brain, Database, FileCheck } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface UploadStep {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'active' | 'completed' | 'error';
  icon: React.ReactNode;
  estimatedTime?: string;
}

interface UploadProgressProps {
  isVisible: boolean;
  currentStep: number;
  progress: number;
  fileName?: string;
  onClose: () => void;
  error?: string;
}

const UploadProgress: React.FC<UploadProgressProps> = ({
  isVisible,
  currentStep,
  progress,
  fileName,
  onClose,
  error
}) => {
  const { isDark } = useTheme();
  
  const steps: UploadStep[] = [
    {
      id: 'upload',
      title: 'Subiendo Archivo',
      description: 'Transfiriendo documento al servidor',
      status: currentStep > 0 ? 'completed' : currentStep === 0 ? 'active' : 'pending',
      icon: <Upload className="w-5 h-5" />,
      estimatedTime: '10s'
    },
    {
      id: 'analyze',
      title: 'Analizando Documento',
      description: 'Extrayendo texto y estructura',
      status: currentStep > 1 ? 'completed' : currentStep === 1 ? 'active' : 'pending',
      icon: <FileCheck className="w-5 h-5" />,
      estimatedTime: '20s'
    },
    {
      id: 'ai',
      title: 'Procesamiento con IA',
      description: 'Identificando ilustraciones, partes y advertencias',
      status: currentStep > 2 ? 'completed' : currentStep === 2 ? 'active' : 'pending',
      icon: <Brain className="w-5 h-5" />,
      estimatedTime: '30s'
    },
    {
      id: 'database',
      title: 'Guardando en Base de Datos',
      description: 'Almacenando información estructurada',
      status: currentStep > 3 ? 'completed' : currentStep === 3 ? 'active' : 'pending',
      icon: <Database className="w-5 h-5" />,
      estimatedTime: '5s'
    },
    {
      id: 'complete',
      title: 'Completado',
      description: 'Documento procesado exitosamente',
      status: currentStep > 4 ? 'completed' : currentStep === 4 ? 'active' : 'pending',
      icon: <CheckCircle className="w-5 h-5" />,
      estimatedTime: ''
    }
  ];

  if (error) {
    steps[currentStep].status = 'error';
  }

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Procesando Documento
            </h2>
            {currentStep >= 4 && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
              >
                Cerrar
              </button>
            )}
          </div>
          {fileName && (
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
              {fileName}
            </p>
          )}
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Progreso general
            </span>
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {Math.round(progress)}%
            </span>
          </div>
          <div className={`w-full bg-gray-200 rounded-full h-3 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div
              className={`h-3 rounded-full transition-all duration-500 ${
                error ? 'bg-red-500' : 'bg-gradient-to-r from-blue-500 to-purple-600'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Steps */}
        <div className="px-6 pb-6 space-y-4 max-h-96 overflow-y-auto">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-start gap-4">
              {/* Icon */}
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                step.status === 'completed' ? 'bg-green-100 text-green-600' :
                step.status === 'active' ? 'bg-blue-100 text-blue-600' :
                step.status === 'error' ? 'bg-red-100 text-red-600' :
                'bg-gray-100 text-gray-400'
              }`}>
                {step.status === 'completed' ? (
                  <CheckCircle className="w-5 h-5" />
                ) : step.status === 'error' ? (
                  <AlertCircle className="w-5 h-5" />
                ) : step.status === 'active' ? (
                  <div className="animate-spin">
                    {step.icon}
                  </div>
                ) : (
                  step.icon
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className={`text-sm font-medium ${
                    step.status === 'completed' ? 'text-green-600' :
                    step.status === 'active' ? 'text-blue-600' :
                    step.status === 'error' ? 'text-red-600' :
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    {step.title}
                  </h3>
                  {step.estimatedTime && step.status === 'active' && (
                    <span className={`text-xs flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      <Clock className="w-3 h-3" />
                      ~{step.estimatedTime}
                    </span>
                  )}
                </div>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>
                  {step.description}
                </p>
                
                {/* Error message */}
                {step.status === 'error' && error && (
                  <p className="text-sm text-red-600 mt-2 p-2 bg-red-50 rounded">
                    {error}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UploadProgress;